import React, { useState, useEffect } from 'react';
import { Form, Formik } from 'formik';
import { Button } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import MobileSidebar from '../../../../components/mobile-layout/mobile-sidebar';
import DropdownPanel from '../../../../components/dropdown-panel';
import { EventTypeFilter } from './EventTypeFilter';
import { DateRangeFilter } from './DateRangeFilter';
import { StatusFilter } from './StatusFilter';
import { SortingOptions } from './SortingOptions';
import styles from './styles.module.scss';

export interface TrackerFilterData {
  eventTypes: string[];
  dateRange: string;
  status: string;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
}

interface TrackerFilterSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  filters: TrackerFilterData;
  onApplyFilters: (filters: TrackerFilterData) => void;
}

const TrackerFilterSidebar: React.FC<TrackerFilterSidebarProps> = ({
  isOpen,
  onClose,
  filters,
  onApplyFilters,
}) => {
  const [localFilters, setLocalFilters] = useState<TrackerFilterData>(filters);

  useEffect(() => {
    if (isOpen) {
      setLocalFilters(filters);
    }
  }, [isOpen, filters]);

  const handleSubmit = (values: TrackerFilterData) => {
    onApplyFilters(values);
    onClose();
  };

  const handleReset = () => {
    const resetFilters: TrackerFilterData = {
      eventTypes: [],
      dateRange: 'all',
      status: 'all',
      sortBy: 'dueDate',
      sortOrder: 'asc',
    };
    setLocalFilters(resetFilters);
    onApplyFilters(resetFilters);
    onClose();
  };

  return (
    <MobileSidebar
      header={
        <div data-testid="tracker-filter-header" role="heading" aria-level={2} className="ml-3">
          <FormattedMessage id="tracker.filter.title" defaultMessage="Filter & Sort" />
        </div>
      }
      isOpen={isOpen}
      onClose={onClose}
    >
      <Formik
        enableReinitialize
        initialValues={localFilters}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue }) => (
          <Form noValidate className={styles.mobileFilterForm}>
            <div className={styles.filterContent}>
              <DropdownPanel title="Event Type" hasBorder>
                <EventTypeFilter
                  selectedTypes={values.eventTypes}
                  onChange={(types) => setFieldValue('eventTypes', types)}
                />
              </DropdownPanel>
              
              <DropdownPanel title="Date Range" hasBorder>
                <DateRangeFilter
                  selectedRange={values.dateRange}
                  onChange={(range) => setFieldValue('dateRange', range)}
                />
              </DropdownPanel>
              
              <DropdownPanel title="Status" hasBorder>
                <StatusFilter
                  selectedStatus={values.status}
                  onChange={(status) => setFieldValue('status', status)}
                />
              </DropdownPanel>
              
              <DropdownPanel title="Sort By">
                <SortingOptions
                  sortBy={values.sortBy}
                  sortOrder={values.sortOrder}
                  onSortByChange={(sortBy) => setFieldValue('sortBy', sortBy)}
                  onSortOrderChange={(order) => setFieldValue('sortOrder', order)}
                />
              </DropdownPanel>
            </div>
            
            <div className={styles.mobileFilterBtns}>
              <Button
                onClick={handleReset}
                variant="btn btn-outline-primary shadow-none"
              >
                <FormattedMessage id="tracker.filter.clear" defaultMessage="Clear" />
              </Button>
              <Button type="submit" variant="btn btn-primary shadow-none">
                <FormattedMessage id="tracker.filter.apply" defaultMessage="Apply" />
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </MobileSidebar>
  );
};

export default TrackerFilterSidebar;